// Чаты: список слева и лента одного разговора. SMY-113
//
// Отправки сообщения здесь нет намеренно — её нет и на сервере. Каждое
// сообщение человека порождает запуск, и заводит его только `POST /v1/runs`
// (см. runs.ts). Две двери в одну ленту разошлись бы на первом же сообщении,
// отправленном мимо запуска.

import type {
  Artifact,
  ArtifactType,
  RatingReason,
  RatingVerdict,
  ScenarioId,
  Source,
  TaskTypeId,
  UnitId,
} from '@smyslika/core'

import type { Client } from './client.ts'
import type { RunOutcome } from './runs.ts'

export type MessageRole = 'user' | 'assistant'
export type MessageKind = 'text' | 'clarification' | 'notice' | 'artifact'

export interface FeedMessage {
  id: string
  role: MessageRole
  kind: MessageKind
  body: string
  /** Запуск, выдавший сообщение. У kind='artifact' по нему и лежит карточка. */
  runId?: string
  /** Имя приложенного файла. Показывается в самом сообщении человека. */
  attachment?: string
  createdAt: string
}

/** Строка списка чатов. */
export interface ChatSummary {
  id: string
  /** Название — первый вопрос человека. Пусто, пока он ничего не написал. */
  title?: string
  projectId?: string
  createdAt: string
  lastMessageAt?: string
  /** Закреплён наверху колонки. */
  pinned?: boolean
  /** Убран в архив: в колонке его нет, в истории — есть. */
  archived?: boolean
}

/**
 * Что случилось с запуском, выдавшим ответ: сколько заняло и списалось ли.
 *
 * Показывается серой строкой прямо над ответом. Раньше на этом месте стояла
 * карточка со всеми этапами — у готового ответа она занимает больше места,
 * чем сам ответ.
 */
export interface RunMeta {
  runId: string
  durationMs?: number
  charged: boolean
  /** Чем списалось. Нужно, чтобы назвать списание словом человека. */
  unit: UnitId
  /** Чем ответ доказан. Строкой внутри пузыря: «Источники · 7». */
  sources: Source[]
  /**
   * На какое число они собраны — дата запуска. Пусто: источников не было.
   * Слова к ней ставит клиент (`sourcesNote` в ядре), сервер шлёт факт.
   */
  sourcesAt?: string
  /** Что человек поставил результату, если ставил. SMY-123 */
  rating?: { verdict: RatingVerdict; reason: RatingReason | null }
  /**
   * Спрашивать ли оценку у этого результата.
   *
   * Решает сервер. Клиент не считает частоту сам: клиентов три, и три разных
   * ответа на «пора ли спросить» сделали бы долю оценённых показателем того,
   * с какого устройства зашли.
   */
  askRating?: boolean
  /**
   * Чем кончился запуск; пусто — ещё идёт. SMY-352
   *
   * По нему узнаётся плашка исхода в ленте — сбой, отказ, отмена, — которую
   * сервер пишет на месте ответа, и решается, ставить ли под ней повтор.
   */
  outcome?: RunOutcome
  /** Каким сценарием шёл запуск: им называется и заводится повтор (SMY-352). */
  scenario: ScenarioId
  /** Запуск — правка карточки: повтора у неё нет (SMY-352). */
  edit?: true
  /**
   * Тип задачи запуска (`assumptionsFor` в ядре). Бывает и у бесплатного
   * уточнения chat-типа, поэтому место блока решает не он, а
   * `assumptionsRunId` — по списанию. SMY-497
   */
  taskType?: TaskTypeId
  /**
   * Ответ собран из прошлого поиска без нового, ISO времени того поиска.
   * Считает сервер; слова — `pastSearchNote` в ядре. SMY-497
   */
  pastSearchAt?: string
}

/**
 * Сохранённый результат внутри ленты.
 *
 * Приезжает вместе с лентой, а не запрашивается по одному: карточки живут
 * прямо в разговоре, и открытие старого чата иначе означало бы по запросу на
 * каждую.
 */
export interface ChatArtifact extends Artifact {
  type: ArtifactType
  runId: string
  scenario: ScenarioId
  unit: UnitId
  charged: boolean
  /** Сколько бесплатных правок израсходовано. Остаток считает экран. */
  editUses: number
  finishedAt?: string
}

export interface ChatFeed {
  id: string
  messages: FeedMessage[]
  artifacts: ChatArtifact[]
  /** Время и списание по каждому запуску чата. */
  runs: RunMeta[]
  /**
   * Сколько уточнений в этом чате ещё бесплатны.
   *
   * Приезжает с лентой, а не отдельной ручкой: поле ввода обязано сказать
   * «уточнение — без списания» ДО отправки.
   */
  freeFollowUpsLeft: number
}

export const createChats = (client: Client) => ({
  list: (opts: { archived?: boolean } = {}) =>
    client.request<{ chats: ChatSummary[] }>(`/v1/chats${opts.archived === true ? '?archived=1' : ''}`),
  /** `signal` — срок экрана (`withDeadline`) у чтения, которого ждёт продолжение правки. */
  feed: (id: string, signal?: AbortSignal) =>
    client.request<ChatFeed>(`/v1/chats/${encodeURIComponent(id)}`, signal != null ? { signal } : {}),

  /** Закрепить, снять закрепление, убрать в архив или вернуть. */
  update: (id: string, changes: { pinned?: boolean; archived?: boolean }) =>
    client.request<{ id: string }>(`/v1/chats/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: changes,
    }),

  /** Удалить чат вместе с перепиской. Журнал списаний при этом остаётся. */
  remove: (id: string) =>
    client.request<{ deleted: true }>(`/v1/chats/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  /** Привязать чат к проекту или отвязать, передав `null`. */
  attach: (id: string, projectId: string | null) =>
    client.request<{ chatId: string; projectId: string | null }>(
      `/v1/chats/${encodeURIComponent(id)}/project`,
      { method: 'POST', body: { projectId } },
    ),
})

export type ChatsApi = ReturnType<typeof createChats>
